<script setup>
import { computed } from 'vue';
import { useMessageContext } from '../../provider.js';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import WhatsappMentionAvatar from '../../WhatsappMentionAvatar.vue';
import { parseWhatsappMentions } from 'dashboard/helper/whatsappMentions';
import { MESSAGE_VARIANTS } from '../../constants';

const props = defineProps({
  content: {
    type: String,
    required: true,
  },
  mentions: { type: Array, default: () => [] },
});

const { variant, conversationId } = useMessageContext();

const formattedContent = computed(() => {
  if (variant.value === MESSAGE_VARIANTS.ACTIVITY) {
    return props.content;
  }

  return new MessageFormatter(props.content).formattedMessage;
});

const mentionParts = computed(() => {
  if (!props.mentions.length) return [];
  const parts = [];
  let last = 0;
  parseWhatsappMentions(props.content).forEach(match => {
    if (match.start > last)
      parts.push({ type: 'text', value: props.content.slice(last, match.start) });
    const { jid } = match;
    const mention = props.mentions.find(item => item.jid === jid);
    if (mention) {
      parts.push({
        type: 'mention',
        jid,
        label: mention.label,
        avatar: mention.avatarUrl || mention.avatar_url,
      });
    } else {
      parts.push({ type: 'text', value: match.raw });
    }
    last = match.end;
  });
  if (last < props.content.length) parts.push({ type: 'text', value: props.content.slice(last) });
  return parts;
});
</script>

<template>
  <span v-if="mentionParts.length" class="prose prose-bubble">
    <template v-for="(part, index) in mentionParts" :key="index">
      <span
        v-if="part.type === 'text'"
        v-dompurify-html="new MessageFormatter(part.value).formattedMessage"
      />
      <span
        v-else
        class="not-prose inline-flex max-w-full items-center gap-1 rounded-full bg-n-alpha-2 px-1.5 py-0.5 align-middle text-sm"
        :title="part.label"
      >
        <WhatsappMentionAvatar
          :conversation-id="conversationId"
          :jid="part.jid"
          :name="part.label"
          :src="part.avatar || ''"
        />
        <span class="max-w-40 truncate">{{ `@${part.label}` }}</span>
      </span>
    </template>
  </span>
  <span v-else v-dompurify-html="formattedContent" class="prose prose-bubble" />
</template>
